import { Box, Icon } from "@chakra-ui/react";
import CDS from "design-system";
import { FiCalendar, FiHome } from "react-icons/fi";
import { IconType } from "react-icons";
import moment from "moment";
import { Participant } from "./types";

interface ColumnHeaderProps {
  participant: Participant;
  slotHeight: number;
}

function getSourceIcon(source: string): IconType | null {
  switch (source) {
    case "google_calendar":
      return FiCalendar;
    case "camino_meetings":
      return FiHome;
    default:
      return null;
  }
}

function getTimezoneAbbreviation(timezone: string | null): string {
  if (!timezone) return "—";
  try {
    const formatter = new Intl.DateTimeFormat("en-US", {
      timeZone: timezone,
      timeZoneName: "short",
    });
    const parts = formatter.formatToParts(new Date());
    const tzPart = parts.find((p) => p.type === "timeZoneName");
    return tzPart?.value || timezone.split("/").pop()?.replace(/_/g, " ") || timezone;
  } catch {
    return timezone.split("/").pop()?.replace(/_/g, " ") || timezone;
  }
}

export default function ColumnHeader({ participant, slotHeight }: ColumnHeaderProps) {
  const SourceIcon = getSourceIcon(participant.availabilitySource);
  const tzLabel = getTimezoneAbbreviation(participant.timezone);

  return (
    <Box
      display="flex"
      flexDirection="column"
      alignItems="center"
      justifyContent="center"
      height={`${slotHeight}px`}
      borderLeftWidth="1px"
      borderLeftColor="gray.200"
      px={1}
      overflow="hidden"
    >
      <Box display="flex" alignItems="center" gap={1}>
        {SourceIcon && <Icon as={SourceIcon} boxSize={3} color="gray.500" />}
        <CDS.Text10 fontWeight="semibold" noOfLines={1}>
          {participant.name}
        </CDS.Text10>
      </Box>
      <CDS.Text10 fontSize="2xs" color="gray.500">
        {tzLabel}
      </CDS.Text10>
      {participant.startDate && (
        <CDS.Text10 fontSize="2xs" color="gray.400">
          Starts {moment(participant.startDate).format("MMM D")}
        </CDS.Text10>
      )}
    </Box>
  );
}
